import { settingsPath } from '../services/platform.js';
import { alertController } from '@ionic/core/components';
import { html } from '../lib/html.js';
import { maskPhone, onlyDigits } from '../lib/masks.js';
import { applyApiFieldErrors, autoClearErrors, busy, confirm, setFieldError, toast, toastError, toastSuccess, valueOf, withLoading } from '../lib/ui.js';
import { location } from '../services/location.js';
import { lookupCep, mapLink, maskCep, showcase, uploadWorkshopPhoto } from '../services/showcase.js';
import { Page, go } from './base.js';
import { bindLinePicker, linePicker, pickedLines } from './workshop.js';
import { sectionTitle } from './maintenance-ui.js';

const DAYS = [
  { day: 1, label: 'Segunda' }, { day: 2, label: 'Terça' }, { day: 3, label: 'Quarta' }, { day: 4, label: 'Quinta' },
  { day: 5, label: 'Sexta' }, { day: 6, label: 'Sábado' }, { day: 0, label: 'Domingo' },
];
const MAX_PHOTOS = 6;
const dateFmt = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });

/** Horário da API → formulário (um intervalo por dia + pausa de almoço igual para todos). */
function hoursToForm(hours) {
  const form = { days: {}, lunch: { on: false, from: '12:00', to: '13:00' } };
  if (!hours?.length) {
    for (const { day } of DAYS) form.days[day] = day === 0 ? { open: false, from: '08:00', to: '12:00' } : day === 6 ? { open: true, from: '08:00', to: '12:00' } : { open: true, from: '08:00', to: '18:00' };
    return form;
  }
  for (const { day } of DAYS) {
    const list = hours.filter((h) => h.day === day).sort((a, b) => a.open.localeCompare(b.open));
    if (!list.length) form.days[day] = { open: false, from: '08:00', to: '18:00' };
    else {
      form.days[day] = { open: true, from: list[0].open, to: list.at(-1).close };
      if (list.length > 1) form.lunch = { on: true, from: list[0].close, to: list[1].open };
    }
  }
  return form;
}

/** Formulário → horário da API. A pausa só vale nos dias em que cabe dentro do expediente. */
function formToHours({ days, lunch }) {
  const out = [];
  for (const { day } of DAYS) {
    const d = days[day];
    if (!d.open) continue;
    if (lunch.on && d.from < lunch.from && lunch.to < d.to && lunch.from < lunch.to) {
      out.push({ day, open: d.from, close: lunch.from }, { day, open: lunch.to, close: d.to });
    } else out.push({ day, open: d.from, close: d.to });
  }
  return out;
}

/**
 * Perfil público da oficina (/oficina/perfil), só o responsável.
 * Fotos e "Estou na oficina agora" salvam na hora; o resto salva no botão do fim.
 */
class WorkshopProfilePage extends Page {
  template() {
    return html`
      <ion-header translucent>
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button default-href="/workshop" text="Voltar"></ion-back-button></ion-buttons>
          <ion-title>Perfil público</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding surface-page" fullscreen>
        <div class="narrow body"></div>
      </ion-content>`;
  }

  setup() {
    const body = this.$('.body');
    body.addEventListener('click', (e) => {
      const el = e.target.closest('[data-action]');
      if (el) this.action(el.dataset.action, el.dataset.id, el);
    });
    body.addEventListener('change', (e) => {
      if (e.target.matches('.pick-photo')) this.addPhotos([...e.target.files]).finally(() => (e.target.value = ''));
      if (e.target.closest('.hours')) this.readHours();
    });
    body.addEventListener('input', (e) => {
      if (e.target.closest('.hours')) this.readHours();
    });
  }

  onEnter() {
    this.load();
  }

  async load() {
    const body = this.$('.body');
    body.innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';
    try {
      const r = await showcase.profile();
      this.p = r.profile;
      this.specialties = r.specialties;
      this.hours = hoursToForm(this.p.hours);
      this.hoursTouched = false;
      this.picked = new Set(this.p.specialties);
      this.render();
    } catch (err) {
      body.innerHTML = html`
        <div class="center">
          <ion-icon name="cloud-offline-outline" class="empty-icon"></ion-icon>
          <p>${err.message}</p>
          <ion-button fill="outline" data-action="reload">Tentar de novo</ion-button>
        </div>`.value;
    }
  }

  render() {
    const p = this.p;
    const body = this.$('.body');
    body.innerHTML = html`
      <div class="completeness"></div>
      ${sectionTitle('Fotos', html`<span class="sec-count">${p.photos.length}/${MAX_PHOTOS}</span>`)}
      <div class="photos-slot"></div>
      ${sectionTitle('Localização no mapa')}
      <div class="location-slot"></div>

      <form class="profile-form" novalidate>
        ${sectionTitle('Endereço')}
        <ion-list lines="none" class="form">
          <ion-input name="address.cep" label="CEP" label-placement="floating" fill="outline" inputmode="numeric" maxlength="9" helper-text="Preenche a rua, o bairro e a cidade"></ion-input>
          <ion-input name="address.street" label="Rua" label-placement="floating" fill="outline" maxlength="120"></ion-input>
          <div class="row">
            <ion-input name="address.number" label="Número" label-placement="floating" fill="outline" maxlength="10" class="num"></ion-input>
            <ion-input name="address.complement" label="Complemento" label-placement="floating" fill="outline" maxlength="60"></ion-input>
          </div>
          <ion-input name="address.district" label="Bairro" label-placement="floating" fill="outline" maxlength="80"></ion-input>
          <div class="row">
            <ion-input name="address.city" label="Cidade" label-placement="floating" fill="outline" maxlength="80"></ion-input>
            <ion-input name="address.state" label="UF" label-placement="floating" fill="outline" maxlength="2" autocapitalize="characters" class="uf"></ion-input>
          </div>
        </ion-list>

        ${sectionTitle('Contato')}
        <ion-list lines="none" class="form">
          <ion-input name="whatsapp" label="WhatsApp" label-placement="floating" fill="outline" inputmode="tel" maxlength="15" helper-text="O botão principal do seu perfil"></ion-input>
          <ion-input name="phone" label="Telefone" label-placement="floating" fill="outline" inputmode="tel" maxlength="15"></ion-input>
        </ion-list>

        ${sectionTitle('Horário de funcionamento')}
        <div class="panel hours">
          ${!p.hours.length ? html`<p class="panel-text">Confira: deixamos um horário comum preenchido. Ele só é salvo se você mexer.</p>` : ''}
          ${DAYS.map(({ day, label }) => {
            const d = this.hours.days[day];
            return html`
              <div class="hours-row">
                <label class="day"><input type="checkbox" data-day="${day}" data-k="open" ${d.open ? 'checked' : ''} />${label}</label>
                <input type="time" data-day="${day}" data-k="from" value="${d.from}" aria-label="${label}: abre" ${d.open ? '' : 'disabled'} />
                <span class="to">às</span>
                <input type="time" data-day="${day}" data-k="to" value="${d.to}" aria-label="${label}: fecha" ${d.open ? '' : 'disabled'} />
              </div>`;
          })}
          <div class="hours-row lunch">
            <label class="day"><input type="checkbox" data-lunch="on" ${this.hours.lunch.on ? 'checked' : ''} />Almoço</label>
            <input type="time" data-lunch="from" value="${this.hours.lunch.from}" aria-label="Almoço: das" ${this.hours.lunch.on ? '' : 'disabled'} />
            <span class="to">às</span>
            <input type="time" data-lunch="to" value="${this.hours.lunch.to}" aria-label="Almoço: até" ${this.hours.lunch.on ? '' : 'disabled'} />
          </div>
        </div>

        ${linePicker(this.p.lines?.length ? this.p.lines : ['LIGHT'])}

        ${sectionTitle('Serviços oferecidos')}
        <div class="tags wrap spec-picker" role="group" aria-label="Serviços oferecidos">
          ${this.specialties.map((s) => html`<button type="button" class="chip ${this.picked.has(s.key) ? 'on' : ''}" aria-pressed="${this.picked.has(s.key) ? 'true' : 'false'}" data-action="spec" data-id="${s.key}">${s.name}</button>`)}
        </div>

        ${sectionTitle('Sobre a empresa')}
        <ion-list lines="none" class="form">
          <ion-textarea name="description" label="Descrição (opcional)" label-placement="floating" fill="outline" maxlength="400" counter="true" auto-grow="true" placeholder="Ex.: negócio familiar desde 2004, revisão com checklist e fotos das peças no app."></ion-textarea>
        </ion-list>

        <ion-button type="submit" expand="block" class="submit">Salvar perfil</ion-button>
        <ion-button expand="block" fill="clear" data-action="preview"><ion-icon slot="start" name="eye-outline"></ion-icon>Ver como os clientes veem</ion-button>
      </form>`.value;

    const f = this.$('form.profile-form');
    autoClearErrors(f);
    bindLinePicker(f);
    const input = (n) => f.querySelector(`[name="${n}"]`);
    const a = p.address ?? {};
    input('address.cep').value = a.cep ? maskCep(a.cep) : '';
    for (const k of ['street', 'number', 'complement', 'district', 'city', 'state']) input(`address.${k}`).value = a[k] ?? '';
    input('whatsapp').value = p.whatsapp ? maskPhone(p.whatsapp.replace(/^55(?=\d{10,11}$)/, '')) : '';
    input('phone').value = p.phone ? maskPhone(p.phone) : '';
    input('description').value = p.description ?? '';
    const mask = (n, fn) => input(n).addEventListener('ionInput', (e) => (e.target.value = fn(e.target.value)));
    mask('whatsapp', maskPhone);
    mask('phone', maskPhone);
    mask('address.state', (v) => String(v ?? '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2));
    input('address.cep').addEventListener('ionInput', (e) => {
      e.target.value = maskCep(e.target.value);
      if (onlyDigits(e.target.value).length === 8) this.fillCep(e.target.value);
    });
    f.addEventListener('submit', (e) => {
      e.preventDefault();
      this.save(f);
    });
    this.renderCompleteness();
    this.renderPhotos();
    this.renderLocation();
  }

  renderCompleteness() {
    const c = this.p.completeness;
    const required = c.missing.filter((m) => m.required);
    this.$('.completeness').innerHTML = html`
      <div class="panel complete-card">
        <div class="complete-head">
          <strong>Perfil ${c.percent}% completo</strong>
          ${this.p.visible ? html`<span class="pill success">Na vitrine</span>` : html`<span class="pill warning">Fora da vitrine</span>`}
        </div>
        <div class="bar" role="progressbar" aria-valuenow="${c.percent}" aria-valuemin="0" aria-valuemax="100"><i style="width:${c.percent}%"></i></div>
        <p class="row-sub">${required.length
          ? `Para aparecer para os motoristas falta: ${required.map((m) => m.label.toLowerCase()).join(', ')}.`
          : this.p.status !== 'APPROVED'
            ? 'Perfil pronto. Ele aparece na vitrine assim que a empresa for aprovada.'
            : c.missing.length
              ? `Para aparecer melhor: ${c.missing.map((m) => m.label.toLowerCase()).join(', ')}.`
              : 'Tudo preenchido. Empresas com fotos e horário recebem mais contatos.'}</p>
      </div>`.value;
  }

  renderPhotos() {
    const photos = this.p.photos;
    this.$('.photos-slot').innerHTML = html`
      <div class="photo-grid">
        ${photos.map((ph, i) => html`
          <button type="button" class="photo-tile" data-action="photo" data-id="${ph.id}" aria-label="Foto ${i + 1}${i === 0 ? ' (capa)' : ''}: opções">
            <img src="${ph.url}" alt="" />
            ${i === 0 ? html`<span class="cover-tag">Capa</span>` : ''}
          </button>`)}
        ${photos.length < MAX_PHOTOS
          ? html`
            <label class="photo-tile add">
              <input type="file" class="pick-photo" accept="image/*" multiple hidden />
              <ion-icon name="camera-outline"></ion-icon><span>Adicionar</span>
            </label>`
          : ''}
      </div>
      <p class="foot-hint">Fachada, área de serviço e equipe. A primeira é a capa. Toque numa foto para trocar a capa ou apagar.</p>`.value;
  }

  renderLocation() {
    const p = this.p;
    const byAddress = p.locationSource === 'ADDRESS';
    this.$('.location-slot').innerHTML = html`
      <div class="panel">
        <div class="row">
          <span class="row-icon ${p.location ? 'success' : 'warning'}"><ion-icon name="${p.location ? 'checkmark-circle-outline' : 'location-outline'}"></ion-icon></span>
          <div class="row-main">
            <span class="row-title">${!p.location ? 'Ainda não marcado' : byAddress ? 'Marcado pelo endereço' : 'Empresa marcada no mapa'}</span>
            <span class="row-sub">${!p.location
              ? 'Preencha o endereço completo e salve: marcamos no mapa sozinhos. É o ponto que os motoristas usam para ver a distância e "Como chegar".'
              : byAddress
                ? html`Posição calculada pelo endereço · <a href="${mapLink(p.location)}" target="_blank" rel="noopener">conferir no mapa</a>. Se o ponto não estiver na porta da empresa, ajuste com o GPS quando estiver lá.`
                : html`${p.locationSource === 'VISIT' ? 'Conferida na visita do selo' : 'Pelo GPS'} em ${dateFmt.format(new Date(p.locationAt))}${p.locationAccuracy ? `, precisão de ${p.locationAccuracy} m` : ''} · <a href="${mapLink(p.location)}" target="_blank" rel="noopener">conferir no mapa</a>`}</span>
          </div>
        </div>
        <div class="panel-foot">
          <ion-button expand="block" fill="outline" data-action="here"><ion-icon slot="start" name="locate-outline"></ion-icon>${byAddress ? 'Ajustar com o GPS (no local)' : 'Estou no local agora (opcional)'}</ion-button>
        </div>
      </div>`.value;
  }

  readHours() {
    const box = this.$('.hours');
    for (const el of box.querySelectorAll('[data-day]')) {
      const d = this.hours.days[el.dataset.day];
      d[el.dataset.k] = el.dataset.k === 'open' ? el.checked : el.value;
    }
    for (const el of box.querySelectorAll('[data-lunch]')) this.hours.lunch[el.dataset.lunch] = el.dataset.lunch === 'on' ? el.checked : el.value;
    for (const el of box.querySelectorAll('input[type="time"][data-day]')) el.disabled = !this.hours.days[el.dataset.day].open;
    for (const el of box.querySelectorAll('input[type="time"][data-lunch]')) el.disabled = !this.hours.lunch.on;
    this.hoursTouched = true;
  }

  async fillCep(cep) {
    const r = await lookupCep(cep);
    const f = this.$('form.profile-form');
    if (!r) return setFieldError(f.querySelector('[name="address.cep"]'), 'CEP não encontrado. Preencha o endereço à mão.');
    for (const [k, v] of Object.entries(r)) if (v) f.querySelector(`[name="address.${k}"]`).value = v;
    f.querySelector('[name="address.number"]').setFocus?.();
  }

  async save(f) {
    const val = (n) => valueOf(f, n);
    const err = (n, m) => setFieldError(f.querySelector(`[name="${n}"]`), m);
    const address = { cep: onlyDigits(val('address.cep')), street: val('address.street'), number: val('address.number'), complement: val('address.complement'), district: val('address.district'), city: val('address.city'), state: val('address.state').toUpperCase() };
    const anyAddress = Object.values(address).some(Boolean);
    let ok = true;
    if (anyAddress) {
      if (address.cep.length !== 8) (ok = false), err('address.cep', 'CEP com 8 números');
      for (const [k, label] of [['street', 'a rua'], ['number', 'o número'], ['district', 'o bairro'], ['city', 'a cidade']]) if (!address[k]) (ok = false), err(`address.${k}`, `Informe ${label}`);
      if (!/^[A-Z]{2}$/.test(address.state)) (ok = false), err('address.state', 'UF');
    }
    const whatsapp = onlyDigits(val('whatsapp'));
    const phone = val('phone');
    if (whatsapp && whatsapp.length < 10) (ok = false), err('whatsapp', 'WhatsApp com DDD');
    if (phone && onlyDigits(phone).length < 10) (ok = false), err('phone', 'Telefone com DDD');
    if (!ok) return toastError('Confira os campos destacados.');

    const patch = {
      whatsapp: whatsapp || (this.p.whatsapp ? '' : undefined), // "" apaga
      description: String(f.querySelector('[name="description"]').value ?? '').trim() || (this.p.description ? '' : undefined),
      specialties: [...this.picked],
    };
    const lines = pickedLines(f);
    if (!lines.length) return (f.querySelector('.line-error').textContent = 'Marque pelo menos uma linha');
    patch.lines = lines;
    if (phone) patch.phone = phone;
    if (anyAddress) {
      if (!address.complement) delete address.complement;
      patch.address = address;
    }
    if (this.hoursTouched || this.p.hours.length) patch.hours = formToHours(this.hours);
    for (const k of Object.keys(patch)) if (patch[k] === undefined) delete patch[k];

    await busy(f.querySelector('.submit'), async () => {
      try {
        const r = await showcase.updateProfile(patch);
        this.p = r.profile;
        this.hoursTouched = false;
        this.renderLocation();
        this.renderCompleteness();
        if (patch.address && !this.p.location) toast('Perfil salvo, mas não achamos esse endereço no mapa. Confira o número e o CEP, ou use "Estou no local agora".', 'warning', 6000);
        else toastSuccess(this.p.visible ? 'Perfil salvo. Já está na vitrine.' : 'Perfil salvo.');
      } catch (e) {
        if (!applyApiFieldErrors(f, e.details)) toastError(e.message);
        else toastError('Confira os campos destacados.');
      }
    });
  }

  async addPhotos(files) {
    const room = MAX_PHOTOS - this.p.photos.length;
    if (files.length > room) toast(`Cabem só mais ${room} foto${room === 1 ? '' : 's'}. Enviando as primeiras.`, 'warning', 3500);
    for (const [i, file] of files.slice(0, room).entries()) {
      try {
        this.p = await withLoading(`Enviando foto ${i + 1} de ${Math.min(files.length, room)}...`, () => uploadWorkshopPhoto(file));
      } catch (e) {
        toastError(e.message);
        break;
      }
    }
    this.$('.photos-slot').previousElementSibling.querySelector('.sec-count').textContent = `${this.p.photos.length}/${MAX_PHOTOS}`;
    this.renderPhotos();
    this.renderCompleteness();
  }

  async photoOptions(id) {
    const i = this.p.photos.findIndex((ph) => ph.id === id);
    const a = await alertController.create({
      header: i === 0 ? 'Foto de capa' : `Foto ${i + 1}`,
      buttons: [
        ...(i > 0 ? [{ text: 'Usar como capa', role: 'cover' }] : []),
        { text: 'Apagar foto', role: 'delete', cssClass: 'alert-button-danger' },
        { text: 'Cancelar', role: 'cancel' },
      ],
    });
    await a.present();
    const { role } = await a.onDidDismiss();
    try {
      if (role === 'cover') {
        const ids = [id, ...this.p.photos.map((ph) => ph.id).filter((x) => x !== id)];
        this.p = (await showcase.reorderPhotos(ids)).profile;
      } else if (role === 'delete') {
        if (!(await confirm({ header: 'Apagar esta foto?', confirmText: 'Apagar', destructive: true }))) return;
        this.p = (await withLoading('Apagando...', () => showcase.removePhoto(id))).profile;
      } else return;
    } catch (e) {
      return toastError(e.message);
    }
    this.$('.photos-slot').previousElementSibling.querySelector('.sec-count').textContent = `${this.p.photos.length}/${MAX_PHOTOS}`;
    this.renderPhotos();
    this.renderCompleteness();
  }

  async markHere() {
    const sure = await confirm({
      header: 'Você está na empresa?',
      message: 'Vamos usar o GPS do celular para marcar a empresa no mapa. Fique na entrada ou dentro dela. Se o celular perguntar, escolha a localização precisa (no iPhone, deixe "Exata" ligado).',
      confirmText: 'Estou aqui',
      cancelText: 'Agora não',
    });
    if (!sure) return;
    try {
      const pos = await withLoading('Buscando o GPS...', () => location.precise());
      this.p = (await withLoading('Salvando...', () => showcase.setLocation(pos))).profile;
      this.renderLocation();
      this.renderCompleteness();
      toastSuccess(`Empresa marcada no mapa (precisão de ${this.p.locationAccuracy} m).`);
    } catch (e) {
      toastError(e.code === 'LOCATION_DENIED' ? `Sem permissão de localização. Libere em ${settingsPath('preciseLocation')}.` : e.message);
    }
  }

  action(name, id, el) {
    if (name === 'reload') return this.load();
    if (name === 'here') return this.markHere();
    if (name === 'photo') return this.photoOptions(id);
    if (name === 'preview') return go(`/oficinas/${this.p.id}`);
    if (name === 'spec') {
      if (this.picked.has(id)) this.picked.delete(id);
      else this.picked.add(id);
      el.classList.toggle('on', this.picked.has(id));
      el.setAttribute('aria-pressed', String(this.picked.has(id)));
    }
  }
}

customElements.define('page-workshop-profile', WorkshopProfilePage);
